/**
 * Performance Optimizer
 * Centralise les optimisations d'INP (Interaction to Next Paint) pour améliorer les performances
 */

class PerformanceOptimizer {
  constructor() {
    this.initialized = false;
    this.deferredTasks = [];
    this.idleCallbacks = [];
  }

  /**
   * Initialise l'optimiseur de performance
   */
  init() {
    if (this.initialized) return;

    this.initialized = true;
    this.setupIdleCallback();
    this.optimizeEventListeners();
    this.setupIntersectionObserver();
  }

  /**
   * Utilise requestIdleCallback pour différer les tâches non critiques
   */
  setupIdleCallback() {
    if ('requestIdleCallback' in window) {
      this.scheduleIdleTasks();
    } else {
      // Fallback pour les navigateurs qui ne supportent pas requestIdleCallback
      setTimeout(() => this.scheduleIdleTasks(), 0);
    }
  }

  /**
   * Programme les tâches à exécuter pendant les périodes d'inactivité
   */
  scheduleIdleTasks() {
    const idleCallback = (deadline) => {
      while (deadline.timeRemaining() > 0 && this.deferredTasks.length > 0) {
        const task = this.deferredTasks.shift();
        try {
          task();
        } catch (error) {
          console.error("Erreur lors de l'exécution d'une tâche différée:", error);
        }
      }

      if (this.deferredTasks.length > 0) {
        this.requestIdleCallback(idleCallback);
      }
    };

    this.requestIdleCallback(idleCallback);
  }

  /**
   * Wrapper pour requestIdleCallback avec fallback
   */
  requestIdleCallback(callback) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback);
    } else {
      setTimeout(callback, 0);
    }
  }

  /**
   * Ajoute une tâche à la file d'attente des tâches différées
   */
  deferTask(task) {
    this.deferredTasks.push(task);

    // Si aucune tâche n'est en cours, démarrer le traitement
    if (this.deferredTasks.length === 1) {
      this.scheduleIdleTasks();
    }
  }

  /**
   * Optimise les event listeners pour réduire l'impact sur l'INP
   */
  optimizeEventListeners() {
    // Optimiser les event listeners de scroll
    this.optimizeScrollListeners();

    // Optimiser les event listeners de resize
    this.optimizeResizeListeners();
  }

  /**
   * Optimise les event listeners de scroll
   */
  optimizeScrollListeners() {
    const scrollElements = new Set();
    let ticking = false;

    const optimizedScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          scrollElements.forEach((element) => {
            if (element.optimizedScrollCallback) {
              element.optimizedScrollCallback();
            }
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    // Méthode globale pour ajouter des listeners de scroll optimisés
    window.addOptimizedScrollListener = (element, callback) => {
      element.optimizedScrollCallback = callback;
      scrollElements.add(element);

      // Ajouter le listener global seulement si c'est le premier élément
      if (scrollElements.size === 1) {
        window.addEventListener('scroll', optimizedScrollHandler, { passive: true });
      }
    };

    // Méthode pour supprimer les listeners
    window.removeOptimizedScrollListener = (element) => {
      scrollElements.delete(element);
      delete element.optimizedScrollCallback;

      // Supprimer le listener global si plus d'éléments
      if (scrollElements.size === 0) {
        window.removeEventListener('scroll', optimizedScrollHandler);
      }
    };
  }

  /**
   * Optimise les event listeners de resize
   */
  optimizeResizeListeners() {
    const resizeElements = new Set();
    let resizeTicking = false;

    const optimizedResizeHandler = () => {
      if (!resizeTicking) {
        requestAnimationFrame(() => {
          resizeElements.forEach((element) => {
            if (element.optimizedResizeCallback) {
              element.optimizedResizeCallback();
            }
          });
          resizeTicking = false;
        });
        resizeTicking = true;
      }
    };

    // Méthode globale pour ajouter des listeners de resize optimisés
    window.addOptimizedResizeListener = (element, callback) => {
      element.optimizedResizeCallback = callback;
      resizeElements.add(element);

      if (resizeElements.size === 1) {
        window.addEventListener('resize', optimizedResizeHandler, { passive: true });
      }
    };

    window.removeOptimizedResizeListener = (element) => {
      resizeElements.delete(element);
      delete element.optimizedResizeCallback;

      if (resizeElements.size === 0) {
        window.removeEventListener('resize', optimizedResizeHandler);
      }
    };
  }

  /**
   * Configure un Intersection Observer global pour optimiser les initialisations
   */
  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    const observedElements = new Map();

    window.globalIntersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const callback = observedElements.get(entry.target);
          if (callback && entry.isIntersecting) {
            // Utiliser requestAnimationFrame pour différer l'exécution
            requestAnimationFrame(() => {
              callback(entry);
            });

            // Arrêter d'observer l'élément après la première intersection
            window.globalIntersectionObserver.unobserve(entry.target);
            observedElements.delete(entry.target);
          }
        });
      },
      {
        rootMargin: '100px',
        threshold: 0.01,
      },
    );

    // Méthode globale pour observer des éléments
    window.observeElement = (element, callback) => {
      observedElements.set(element, callback);
      window.globalIntersectionObserver.observe(element);
    };

    // Méthode pour arrêter d'observer
    window.unobserveElement = (element) => {
      window.globalIntersectionObserver.unobserve(element);
      observedElements.delete(element);
    };
  }

  /**
   * Optimise les manipulations DOM en les groupant
   */
  static batchDOMUpdates(updates) {
    requestAnimationFrame(() => {
      updates.forEach((update) => {
        try {
          update();
        } catch (error) {
          console.error('Erreur lors de la mise à jour DOM:', error);
        }
      });
    });
  }

  /**
   * Debounce optimisé avec requestAnimationFrame
   */
  static debounce(func, wait = 100, immediate = false) {
    let timeout;
    let rafId;

    return function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) {
          rafId = requestAnimationFrame(() => func.apply(this, args));
        }
      };

      const callNow = immediate && !timeout;

      clearTimeout(timeout);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      timeout = setTimeout(later, wait);

      if (callNow) {
        rafId = requestAnimationFrame(() => func.apply(this, args));
      }
    };
  }

  /**
   * Throttle optimisé avec requestAnimationFrame
   */
  static throttle(func, limit = 16) {
    let inThrottle;
    let rafId;

    return function (...args) {
      if (!inThrottle) {
        rafId = requestAnimationFrame(() => {
          func.apply(this, args);
          inThrottle = false;
        });
        inThrottle = true;
      }
    };
  }
}

// Initialiser l'optimiseur de performance
const performanceOptimizer = new PerformanceOptimizer();

// Initialiser après le chargement du DOM
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    performanceOptimizer.init();
  });
} else {
  performanceOptimizer.init();
}

// Exporter pour utilisation globale
window.PerformanceOptimizer = PerformanceOptimizer;
window.performanceOptimizer = performanceOptimizer;
